import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { fetchAccess, removeAccess, setAccess, type Access } from "./api.js";

/**
 * Qui a accès au site, et à quel titre — écran du propriétaire.
 *
 * Avant, la liste vivait dans deux secrets Secret Manager : inviter quelqu'un demandait une
 * commande `gcloud` puis un déploiement. Elle se règle maintenant ici, et prend effet en
 * moins d'une minute (le serveur relit la liste toutes les 30 secondes).
 *
 * Le propriétaire est affiché mais **non modifiable** : son rôle vient de la configuration du
 * serveur, pour qu'aucun clic dans cet écran ne puisse le verrouiller dehors de son propre
 * site. Les protections réelles sont côté serveur (`apps/api/src/access.ts`) — ce qu'on grise
 * ici n'est qu'un rappel.
 */

/** « 26 juil. 2026 » — la date compte, l'heure non. */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

const ROLES: { value: Access["role"]; label: string; aide: string }[] = [
  { value: "member", label: "Voir", aide: "Voit tout, commente, corrige. Ne range pas." },
  { value: "editor", label: "Ranger", aide: "En plus : crée des albums et y classe les photos." },
];

export function AccessPanel({ onClose }: { onClose: () => void }) {
  const [access, setAccessList] = useState<Access[] | null>(null);
  const [owner, setOwner] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [role, setRole] = useState<Access["role"]>("member");
  const [erreur, setErreur] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = async () => {
    const d = await fetchAccess();
    setAccessList(d.access);
    setOwner(d.owner);
  };
  useEffect(() => {
    void load();
  }, []);

  const inviter = async () => {
    const email = nouveau.trim().toLowerCase();
    if (!email) return;
    setErreur(null);
    setBusy(email);
    try {
      await setAccess(email, role);
      setNouveau("");
      await load();
    } catch {
      setErreur(`Impossible d'ajouter ${email}.`);
    } finally {
      setBusy(null);
    }
  };

  const changer = async (a: Access, r: Access["role"]) => {
    setBusy(a.email);
    try {
      await setAccess(a.email, r);
      await load();
    } finally {
      setBusy(null);
    }
  };

  const retirer = async (a: Access) => {
    setBusy(a.email);
    try {
      await removeAccess(a.email);
      setConfirmId(null);
      await load();
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <div className="modal" data-testid="access-panel" onClick={onClose}>
      <div className="modal__card modal__card--wide" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h3 className="modal__title">Accès</h3>
          <button className="modal__close" onClick={onClose} aria-label="Fermer">×</button>
        </header>

        <div className="access__invite">
          <input
            className="access__email"
            type="email"
            placeholder="prenom@exemple.com"
            value={nouveau}
            data-testid="access-email"
            onChange={(e) => setNouveau(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void inviter();
            }}
          />
          <select
            className="access__role"
            value={role}
            onChange={(e) => setRole(e.target.value as Access["role"])}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            className="access__add"
            data-testid="access-add"
            disabled={!nouveau.trim() || busy !== null}
            onClick={() => void inviter()}
          >
            Inviter
          </button>
        </div>
        <p className="modal__muted access__aide">
          {ROLES.find((r) => r.value === role)?.aide} Cette personne devra se connecter avec ce
          compte Google.
        </p>
        {erreur && <p className="access__erreur">{erreur}</p>}

        <ul className="access__list">
          <li className="access__row access__row--owner">
            <span className="access__who">
              {owner}
              <span className="access__sub">Propriétaire — non modifiable</span>
            </span>
            <span className="modal__muted">Tout</span>
          </li>
          {access?.map((a) => (
            <li key={a.id} className="access__row" data-testid="access-row">
              <span className="access__who">
                {a.email}
                {/* Les lignes reprises de l'ancienne liste n'ont pas d'auteur à citer, mais
                    elles ont une date : celle de la création du secret d'origine. */}
                {a.addedAt && (
                  <span className="access__sub">
                    depuis le {fmtDate(a.addedAt)}
                    {a.addedBy !== "graine" && a.addedBy !== "secret" && `, par ${a.addedBy}`}
                  </span>
                )}
              </span>
              <select
                className="access__role"
                value={a.role}
                disabled={busy === a.email}
                data-testid="access-role"
                onChange={(e) => void changer(a, e.target.value as Access["role"])}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {confirmId === a.id ? (
                <span className="access__confirm">
                  Retirer l'accès ?
                  <button className="access__yes" onClick={() => void retirer(a)}>Oui</button>
                  <button className="access__no" onClick={() => setConfirmId(null)}>Non</button>
                </span>
              ) : (
                <button
                  className="access__remove"
                  data-testid="access-remove"
                  disabled={busy === a.email}
                  onClick={() => setConfirmId(a.id)}
                >
                  Retirer
                </button>
              )}
            </li>
          ))}
        </ul>
        {access?.length === 0 && <p className="modal__muted">Personne d'autre pour l'instant.</p>}
      </div>
    </div>,
    document.body,
  );
}
